import { useCallback, useSyncExternalStore } from "react";

/**
 * Read a single URL search param without Next.js `useSearchParams`.
 *
 * `useSearchParams` suspends during static/SSR and forces a Suspense boundary.
 * Wrapping the shared shell in `<Suspense fallback={null}>` deferred Header/main
 * behind a streaming hole and contributed to React #418 hydration mismatches.
 *
 * SSR + the first client render both return `null` (server snapshot). After
 * hydration, the live `window.location.search` value is used. Soft navigations
 * are observed via a shared History API patch + `popstate`.
 */

const urlListeners = new Set<() => void>();
let historyPatched = false;
let originalPushState: History["pushState"] | null = null;
let originalReplaceState: History["replaceState"] | null = null;
/** Last observed search string — skip notify when third parties rewrite history without changing params. */
let lastNotifiedSearch: string | null = null;
let notifyScheduled = false;

/**
 * Defer store notifications off the history API call stack.
 * Third-party scripts (e.g. Cuoral) may call replaceState during React's
 * useInsertionEffect; sync useSyncExternalStore updates there throw.
 */
function scheduleNotifyUrlListeners() {
  if (typeof window === "undefined") return;
  const nextSearch = window.location.search;
  if (nextSearch === lastNotifiedSearch) return;
  if (notifyScheduled) return;
  notifyScheduled = true;
  queueMicrotask(() => {
    notifyScheduled = false;
    const search = window.location.search;
    if (search === lastNotifiedSearch) return;
    lastNotifiedSearch = search;
    urlListeners.forEach((listener) => listener());
  });
}

function ensureHistoryPatched() {
  if (historyPatched || typeof window === "undefined") return;
  historyPatched = true;
  lastNotifiedSearch = window.location.search;
  originalPushState = window.history.pushState.bind(window.history);
  originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = function pushStatePatched(
    ...args: Parameters<History["pushState"]>
  ) {
    const result = originalPushState!(...args);
    scheduleNotifyUrlListeners();
    return result;
  };
  window.history.replaceState = function replaceStatePatched(
    ...args: Parameters<History["replaceState"]>
  ) {
    const result = originalReplaceState!(...args);
    scheduleNotifyUrlListeners();
    return result;
  };
  window.addEventListener("popstate", scheduleNotifyUrlListeners);
}

function readQueryParam(name: string): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(name);
}

function subscribeToUrl(onStoreChange: () => void) {
  ensureHistoryPatched();
  urlListeners.add(onStoreChange);
  return () => {
    urlListeners.delete(onStoreChange);
  };
}

export function useQueryParam(name: string): string | null {
  const getSnapshot = useCallback(() => readQueryParam(name), [name]);
  return useSyncExternalStore(subscribeToUrl, getSnapshot, () => null);
}
